import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputNumber } from 'antd';
import {
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Edit,
  GripVertical,
  HelpCircle,
  Loader2,
  Plus,
  Search,
  Settings2,
  Trash2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as z from 'zod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent } from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BaseAssessment,
  CreateAssessment,
  CreateAssessmentItem,
  UpdateAssessment,
  UpdateAssessmentItem,
} from '../dtos/Assessments/index';
import { AnswerInterval, AssessmentItemRule, AssessmentRule, ScoreInterval } from '../dtos/Rules/index';
import {
  useCreateAssessment,
  useDeleteAssessment,
  useGetAssessmentsPaged,
  useGetRegistryTerms,
  useUpdateAssessment,
} from '../hooks/Assessment';
import { usePermissions } from '../hooks/usePermissions';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const VALUE_TYPES = ['string', 'bool', 'int', 'numeric', 'checkList'];
const PERSON_PROFILE_OPTIONS = [
  { label: 'Varsta', value: 'age' },
  { label: 'Sex', value: 'gender' },
  { label: 'Regiune', value: 'region' },
  { label: 'Este Fumator', value: 'isSmoker' },
  { label: 'Este Fost Fumator', value: 'isFormerSmoker' },
  { label: 'Are Diabet', value: 'hasDiabetes' },
  { label: 'Are Hipertensiune', value: 'hasHypertension' },
  { label: 'Are Dislipidemie', value: 'hasDyslipidemia' },
  { label: 'Indice Masa Corporala', value: 'bodyMassIndex' },
  { label: 'Presiune Arginala Sistolica', value: 'systolicBloodPressure' },
  { label: 'Isotirc Familie Boli Cardiovasculare', value: 'familyHistoryCardiovascularDisease' },
];

const isBooleanAnswerType = (answerType?: string) => {
  const normalized = answerType?.toLowerCase() || '';
  return normalized === 'bool' || normalized === 'boolean';
};

const isNumericAnswerType = (answerType?: string) => {
  const normalized = answerType?.toLowerCase() || '';
  return normalized === 'int' || normalized === 'numeric' || normalized === 'double' || normalized === 'number';
};

const isChecklistAnswerType = (answerType?: string) => {
  const normalized = answerType?.toLowerCase() || '';
  return normalized === 'checklist';
};

const normalizeAnswerTypeForBackend = (answerType?: string) => {
  if (!answerType) return undefined;
  return answerType === 'numeric' ? 'double' : answerType;
};

const assessmentSchema = z.object({
  name: z.string().min(1, 'Numele este obligatoriu'),
  specialization: z.string().optional(),
  version: z.coerce.number().optional(),
});

type AssessmentFormValues = z.infer<typeof assessmentSchema>;

interface AnswerRuleForm {
  isOptional: boolean;
  hasInterval: boolean;
  min?: number;
  max?: number;
}

interface RiskForm {
  low: ScoreInterval;
  medium: ScoreInterval;
  high: ScoreInterval;
  veryHigh: ScoreInterval;
}

const DEFAULT_RISKS: RiskForm = {
  low: { min: 1, max: 25 },
  medium: { min: 25, max: 50 },
  high: { min: 50, max: 75 },
  veryHigh: { min: 75, max: 100 },
};

interface AnswerOptionForm {
  displayValue: string;
  score: number;
  rule: AnswerRuleForm;
}

interface AssessmentItemForm {
  id?: string;
  tempId: string; // For drag and drop identification
  orderIndex: number;
  question: string;
  registryTerm?: string;
  answerType?: string;
  answerPersonProfile?: string;
  answers: AnswerOptionForm[];
}

const getValueTypeLabel = (type: string) => {
  switch (type) {
    case 'string':
      return 'Text';
    case 'bool':
      return 'Da/Nu';
    case 'int':
      return 'Număr întreg';
    case 'numeric':
      return 'Număr zecimal';
    case 'checkList':
      return 'Listă';
    default:
      return type;
  }
};

const createDefaultRule = (): AnswerRuleForm => ({
  isOptional: false,
  hasInterval: false,
  min: undefined,
  max: undefined,
});

// Generate unique temp ID
let tempIdCounter = 0;
const generateTempId = () => `temp-${Date.now()}-${++tempIdCounter}`;

// Sortable Question Item Component
function SortableQuestionItem({
  item,
  index,
  isActive,
  onClick,
}: {
  item: AssessmentItemForm;
  index: number;
  isActive: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.tempId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasRules = item.answers.some((a) => a.rule.isOptional || a.rule.hasInterval);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 p-2 rounded cursor-pointer transition-colors ${
        isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
      } ${isDragging ? 'shadow-lg' : ''}`}
      onClick={onClick}
    >
      <div
        {...attributes}
        {...listeners}
        className='cursor-grab active:cursor-grabbing'
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className='h-3 w-3 opacity-50' />
      </div>
      <span className='text-xs text-muted-foreground w-5'>{item.orderIndex}</span>
      <span className='flex-1 truncate text-sm'>{item.question || `Întrebare ${index + 1}`}</span>
      <div className='flex items-center gap-0.5'>
        {hasRules && <Settings2 className={`h-3 w-3 ${isActive ? 'text-primary-foreground' : 'text-primary'}`} />}
        <Badge variant={isActive ? 'secondary' : 'outline'} className='text-xs'>
          {item.answers.length}
        </Badge>
      </div>
    </div>
  );
}

// Single Answer Option with Rules
function AnswerOptionRow({
  option,
  index,
  onUpdate,
  onRemove,
  idPrefix,
  answerType,
}: {
  option: AnswerOptionForm;
  index: number;
  onUpdate: (field: string, value: unknown) => void;
  onRemove: () => void;
  idPrefix: string;
  answerType?: string;
}) {
  const [rulesOpen, setRulesOpen] = useState(option.rule.isOptional || option.rule.hasInterval);
  const isBool = isBooleanAnswerType(answerType);
  const isNumeric = isNumericAnswerType(answerType);
  const isNumericStep = answerType?.toLowerCase() === 'numeric';

  return (
    <div className='border rounded bg-muted/30 p-2'>
      {/* ROW 1 */}
      <div className='grid grid-cols-[28px_120px_1fr_140px_80px_72px] items-center gap-2'>
        {/* index */}
        <span className='text-xs text-muted-foreground text-center'>{index}</span>

        {/* value */}
        <div className='min-w-0'>
          {isBool ? (
            <Select value={option.displayValue} onValueChange={(value) => onUpdate('displayValue', value)}>
              <SelectTrigger className='h-9 text-sm'>
                <SelectValue placeholder='Valoare' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='Da'>Da</SelectItem>
                <SelectItem value='Nu'>Nu</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              type='text'
              placeholder={answerType === 'string' ? 'Introduceți text' : 'Valoare'}
              value={option.displayValue}
              onChange={(e) => onUpdate('displayValue', e.target.value)}
              className='h-9 text-sm'
            />
          )}
        </div>

        {/* score */}
        <InputNumber
          placeholder='Scor'
          value={option.score}
          onChange={(e) => onUpdate('score', e)}
          className='w-full h-9 text-sm'
        />

        {/* rules btn */}
        <Button type='button' variant='outline' size='sm' onClick={() => setRulesOpen(!rulesOpen)} title='Reguli'>
          <Settings2 className='mr-0' />
        </Button>

        {/* delete btn */}
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-9 text-destructive hover:text-destructive hover:bg-destructive/10'
          onClick={onRemove}
          title='Șterge'
        >
          <Trash2 className='h-4 w-4' />
        </Button>
      </div>

      {/* ROW 2 (numeric interval) */}
      {isNumeric && (
        <div className='mt-2 grid grid-cols-[28px_120px_1fr] gap-2 items-start'>
          <div />
          <div className='text-xs text-muted-foreground pt-2'>Interval</div>

          <div className='space-y-1'>
            <div className='flex flex-wrap gap-2 items-center'>
              <div className='flex items-center gap-2'>
                <Label className='text-xs text-muted-foreground w-10'>Min</Label>
                <Input
                  type='number'
                  step={isNumericStep ? '0.01' : '1'}
                  value={option.rule.min ?? ''}
                  onChange={(e) =>
                    onUpdate('rule', {
                      ...option.rule,
                      min: e.target.value ? parseFloat(e.target.value) : undefined,
                      hasInterval: true,
                    })
                  }
                  className='h-9 w-28 text-sm'
                  placeholder='Min'
                />
              </div>

              <div className='flex items-center gap-2'>
                <Label className='text-xs text-muted-foreground w-10'>Max</Label>
                <Input
                  type='number'
                  step={isNumericStep ? '0.01' : '1'}
                  value={option.rule.max ?? ''}
                  onChange={(e) =>
                    onUpdate('rule', {
                      ...option.rule,
                      max: e.target.value ? parseFloat(e.target.value) : undefined,
                      hasInterval: true,
                    })
                  }
                  className='h-9 w-28 text-sm'
                  placeholder='Max'
                />
              </div>
            </div>

            {/* validation message */}
            {option.displayValue &&
              option.rule.min !== undefined &&
              option.rule.max !== undefined &&
              (() => {
                const numValue = isNumericStep ? parseFloat(option.displayValue) : parseInt(option.displayValue, 10);

                if (!isNaN(numValue) && (numValue < option.rule.min! || numValue > option.rule.max!)) {
                  return (
                    <p className='text-xs text-destructive'>
                      Valoarea trebuie să fie între {option.rule.min} și {option.rule.max}
                    </p>
                  );
                }
                return null;
              })()}
          </div>
        </div>
      )}

      {/* Rules Section (unchanged, but now visually separated) */}
      <Collapsible open={rulesOpen} onOpenChange={setRulesOpen}>
        <CollapsibleContent>
          <div className='mt-3 px-2 pb-2 pt-2 border-t border-dashed space-y-3'>
            <p className='text-xs font-medium text-muted-foreground'>Reguli pentru răspunsul #{index}</p>

            <div className='flex items-center space-x-2'>
              <Checkbox
                id={`${idPrefix}-optional-${index}`}
                checked={option.rule.isOptional}
                onCheckedChange={(checked) => onUpdate('rule', { ...option.rule, isOptional: !!checked })}
              />
              <Label htmlFor={`${idPrefix}-optional-${index}`} className='text-sm font-normal'>
                Răspuns opțional (se adaugă în optionalAnswers)
              </Label>
            </div>

          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// Answer Options Section Component
function AnswerOptionsSection({
  options,
  onAdd,
  onUpdate,
  onRemove,
  idPrefix,
  answerType,
}: {
  options: AnswerOptionForm[];
  onAdd: () => void;
  onUpdate: (index: number, field: string, value: unknown) => void;
  onRemove: (index: number) => void;
  idPrefix: string;
  answerType?: string;
}) {
  const isBool = isBooleanAnswerType(answerType);
  const canAdd = !isBool || options.length < 2;

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <Label className='text-sm'>Opțiuni Răspuns</Label>
        {canAdd && (
          <Button type='button' variant='outline' size='sm' onClick={onAdd}>
            <Plus className='h-3 w-3 mr-1' />
            Adaugă
          </Button>
        )}
      </div>
      {options.length === 0 ? (
        <p className='text-xs text-muted-foreground'>Nu există opțiuni.</p>
      ) : (
        <div className='space-y-1.5'>
          {options.map((option, index) => (
            <AnswerOptionRow
              key={`${idPrefix}-${index}`}
              option={option}
              index={index}
              onUpdate={(field, value) => onUpdate(index, field, value)}
              onRemove={() => onRemove(index)}
              idPrefix={idPrefix}
              answerType={answerType}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Assessments() {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BaseAssessment | null>(null);

  // Items management state
  const [items, setItems] = useState<AssessmentItemForm[]>([]);
  const [activeItemIndex, setActiveItemIndex] = useState<number>(0);

  // Risks management state
  const [isRisksDialogOpen, setIsRisksDialogOpen] = useState(false);
  const [risks, setRisks] = useState<RiskForm>(DEFAULT_RISKS);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const searchTerm = searchParams.get('search') || '';

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const { data: response, isLoading } = useGetAssessmentsPaged(page, pageSize, searchTerm || undefined);
  const { data: registryTermsResponse } = useGetRegistryTerms();
  const createMutation = useCreateAssessment();
  const updateMutation = useUpdateAssessment();
  const deleteMutation = useDeleteAssessment();

  const registryTerms = registryTermsResponse?.isSuccess ? registryTermsResponse.data?.terms : undefined;

  const pagedData = response?.isSuccess ? response.data : null;
  const assessments = pagedData?.items || [];
  const totalCount = pagedData?.totalCount || 0;
  const totalPages = pagedData?.totalPages || 1;

  const updateParams = (updates: { page?: number; size?: number; search?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    if (updates.page !== undefined) {
      if (updates.page === 1) newParams.delete('page');
      else newParams.set('page', updates.page.toString());
    }
    if (updates.size !== undefined) {
      if (updates.size === 10) newParams.delete('size');
      else newParams.set('size', updates.size.toString());
    }
    if (updates.search !== undefined) {
      if (updates.search === '') newParams.delete('search');
      else newParams.set('search', updates.search);
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const handleSearch = () => updateParams({ search: searchInput });
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const clearSearch = () => {
    setSearchInput('');
    updateParams({ search: '' });
  };

  const createForm = useForm<AssessmentFormValues>({
    resolver: zodResolver(assessmentSchema),
    defaultValues: { name: '', specialization: '', version: 1 },
  });

  const editForm = useForm<AssessmentFormValues>({
    resolver: zodResolver(assessmentSchema),
    defaultValues: { name: '', specialization: '', version: 1 },
  });

  // Item management functions
  const addItem = () => {
    const newItem: AssessmentItemForm = {
      tempId: generateTempId(),
      orderIndex: items.length + 1,
      question: '',
      registryTerm: '',
      answerType: 'string',
      answerPersonProfile: '',
      answers: [],
    };
    setItems([...items, newItem]);
    setActiveItemIndex(items.length);
  };

  // Handle drag end - reorder items
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.tempId === active.id);
      const newIndex = items.findIndex((item) => item.tempId === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex).map((item, i) => ({
          ...item,
          orderIndex: i + 1,
        }));
        setItems(newItems);

        // Update active index if needed
        if (activeItemIndex === oldIndex) {
          setActiveItemIndex(newIndex);
        } else if (activeItemIndex > oldIndex && activeItemIndex <= newIndex) {
          setActiveItemIndex(activeItemIndex - 1);
        } else if (activeItemIndex < oldIndex && activeItemIndex >= newIndex) {
          setActiveItemIndex(activeItemIndex + 1);
        }
      }
    }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const removeItem = (index: number) => {
    const newItems = items
      .filter((_, i) => i !== index)
      .map((item, i) => ({
        ...item,
        orderIndex: i + 1,
      }));
    setItems(newItems);
    if (activeItemIndex >= newItems.length) {
      setActiveItemIndex(Math.max(0, newItems.length - 1));
    }
  };

  const updateItem = (index: number, field: keyof AssessmentItemForm, value: unknown) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const handleAnswerTypeChange = (index: number, value: string) => {
    const newItems = [...items];
    const nextAnswerType = value;
    const isNumeric = isNumericAnswerType(nextAnswerType);
    const isBool = isBooleanAnswerType(nextAnswerType);

    newItems[index] = {
      ...newItems[index],
      answerType: nextAnswerType,
      answers: newItems[index].answers.map((answer) => {
        const displayValue =
          isBool && !['da', 'nu'].includes(answer.displayValue.toLowerCase())
            ? ''
            : answer.displayValue;
        return {
          ...answer,
          displayValue,
          rule: isNumeric
            ? answer.rule
            : {
                ...answer.rule,
                hasInterval: false,
                min: undefined,
                max: undefined,
              },
        };
      }),
    };

    setItems(newItems);
  };

  const addAnswerOption = (itemIndex: number) => {
    const newItems = [...items];
    const answerType = newItems[itemIndex].answerType;
    const isBool = isBooleanAnswerType(answerType);
    let defaultDisplayValue = '';

    if (isBool) {
      const existingValues = newItems[itemIndex].answers.map((ans) => ans.displayValue.toLowerCase());
      if (!existingValues.includes('da')) defaultDisplayValue = 'Da';
      else if (!existingValues.includes('nu')) defaultDisplayValue = 'Nu';
    }

    newItems[itemIndex].answers = [
      ...newItems[itemIndex].answers,
      { displayValue: defaultDisplayValue, score: 0, rule: createDefaultRule() },
    ];
    setItems(newItems);
  };

  const updateAnswerOption = (itemIndex: number, answerIndex: number, field: string, value: unknown) => {
    const newItems = [...items];
    const answer = newItems[itemIndex].answers[answerIndex];
    if (field === 'rule') {
      answer.rule = value as AnswerRuleForm;
    } else if (field === 'displayValue') {
      answer.displayValue = value as string;
    } else if (field === 'score') {
      answer.score = value as number;
    }

    setItems(newItems);
  };

  const removeAnswerOption = (itemIndex: number, answerIndex: number) => {
    const newItems = [...items];
    newItems[itemIndex].answers = newItems[itemIndex].answers.filter((_, i) => i !== answerIndex);
    setItems(newItems);
  };

  // Build rules from answers
  const buildRulesFromAnswers = (
    answers: AnswerOptionForm[],
    answerType?: string
  ): AssessmentItemRule | undefined => {
    const optionalAnswers: Record<number, boolean> = {};
    const intervalAnswers: Record<number, AnswerInterval> = {};
    const isNumeric = isNumericAnswerType(answerType);
    const isBool = isBooleanAnswerType(answerType);
    const isChecklist = isChecklistAnswerType(answerType);

    answers.forEach((ans, index) => {
      if (ans.rule.isOptional) {
        optionalAnswers[index] = true;
      }
      // For int/numeric types, min/max should always be included if set
      if (!isBool && !isChecklist && isNumeric) {
        if (ans.rule.min !== undefined || ans.rule.max !== undefined) {
          intervalAnswers[index] = {
            min: ans.rule.min,
            max: ans.rule.max,
          };
        }
      }
    });

    const hasOptional = Object.keys(optionalAnswers).length > 0;
    const hasInterval = Object.keys(intervalAnswers).length > 0;

    if (!hasOptional && !hasInterval) {
      return undefined;
    }

    return {
      optionalAnswers: hasOptional ? optionalAnswers : undefined,
      intervalAnswers: hasInterval ? intervalAnswers : undefined,
    };
  };

  const updateRisk = (riskType: keyof RiskForm, field: 'min' | 'max', value: number | undefined) => {
    setRisks((prev) => {
      const updated = { ...prev };
      updated[riskType] = { ...updated[riskType], [field]: value };

      // Auto-sync intervals to maintain sequential order
      if (riskType === 'low' && field === 'max') {
        // When low.max changes, update medium.min
        updated.medium = { ...updated.medium, min: value };
      } else if (riskType === 'medium') {
        if (field === 'min') {
          // When medium.min changes, update low.max
          updated.low = { ...updated.low, max: value };
        } else if (field === 'max') {
          // When medium.max changes, update high.min
          updated.high = { ...updated.high, min: value };
        }
      } else if (riskType === 'high') {
        if (field === 'min') {
          // When high.min changes, update medium.max
          updated.medium = { ...updated.medium, max: value };
        } else if (field === 'max') {
          // When high.max changes, update veryHigh.min
          updated.veryHigh = { ...updated.veryHigh, min: value };
        }
      } else if (riskType === 'veryHigh' && field === 'min') {
        // When veryHigh.min changes, update high.max
        updated.high = { ...updated.high, max: value };
      }

      return updated;
    });
  };

  const buildAssessmentRules = (): AssessmentRule | undefined => {
    // Ensure all risk intervals are properly defined
    const intervalScores: Record<string, ScoreInterval> = {
      low: {
        min: risks.low.min ?? undefined,
        max: risks.low.max ?? undefined,
      },
      medium: {
        min: risks.medium.min ?? undefined,
        max: risks.medium.max ?? undefined,
      },
      high: {
        min: risks.high.min ?? undefined,
        max: risks.high.max ?? undefined,
      },
      veryHigh: {
        min: risks.veryHigh.min ?? undefined,
        max: risks.veryHigh.max ?? undefined,
      },
    };
    return { intervalScores };
  };

  const handleCreate = async (values: AssessmentFormValues) => {
    const assessmentItems: CreateAssessmentItem[] = items
      .filter((item) => item.question.trim() !== '')
      .map((item) => {
        const isNumeric = isNumericAnswerType(item.answerType);

        // For numeric types, keep answers with min/max even if displayValue is empty
        // For other types, filter out empty displayValues
        const filteredAnswers = item.answers
          .filter((ans) => {
            // Keep if displayValue is not empty
            if (ans.displayValue.trim() !== '') return true;
            // For int/numeric, keep if there are min/max rules
            if (isNumeric && (ans.rule.min !== undefined || ans.rule.max !== undefined)) {
              return true;
            }
            return false;
          })
          .map((ans) => ({
            displayValue: ans.displayValue || '', // Ensure it's at least empty string
            score: ans.score,
          }));

        // Build rules from all answers (including those that might be filtered)
        const rules = buildRulesFromAnswers(item.answers, item.answerType);

        return {
          orderIndex: item.orderIndex,
          question: item.question,
          registryTerm: item.registryTerm || undefined,
          answerType: normalizeAnswerTypeForBackend(item.answerType),
          answerPersonProfile: item.answerPersonProfile || undefined,
          answers: filteredAnswers.length > 0 ? filteredAnswers : undefined,
          rules: rules,
        };
      });

    const data: CreateAssessment = {
      name: values.name,
      specialization: values.specialization || undefined,
      version: values.version,
      rules: buildAssessmentRules(),
      items: assessmentItems.length > 0 ? assessmentItems : undefined,
    };
    const res = await createMutation.mutateAsync(data);
    if (res.isSuccess) {
      setIsCreateDialogOpen(false);
      createForm.reset();
      setItems([]);
      setActiveItemIndex(0);
      setRisks(DEFAULT_RISKS);
    }
  };

  const handleEdit = async (values: AssessmentFormValues) => {
    if (!selectedItem) return;

    const assessmentItems: UpdateAssessmentItem[] = items
      .filter((item) => item.question.trim() !== '')
      .map((item) => {
        const isNumeric = isNumericAnswerType(item.answerType);

        // For numeric types, keep answers with min/max even if displayValue is empty
        // For other types, filter out empty displayValues
        const filteredAnswers = item.answers
          .filter((ans) => {
            // Keep if displayValue is not empty
            if (ans.displayValue.trim() !== '') return true;
            // For int/numeric, keep if there are min/max rules
            if (isNumeric && (ans.rule.min !== undefined || ans.rule.max !== undefined)) {
              return true;
            }
            return false;
          })
          .map((ans) => ({
            displayValue: ans.displayValue || '', // Ensure it's at least empty string
            score: ans.score,
          }));

        // Build rules from all answers (including those that might be filtered)
        const rules = buildRulesFromAnswers(item.answers, item.answerType);

        // Only include id if it exists (for existing items)
        const result: UpdateAssessmentItem = {
          orderIndex: item.orderIndex,
          question: item.question,
          registryTerm: item.registryTerm || undefined,
          answerType: normalizeAnswerTypeForBackend(item.answerType),
          answerPersonProfile: item.answerPersonProfile || undefined,
          answers: filteredAnswers.length > 0 ? filteredAnswers : undefined,
          rules: rules,
        };

        // Only add id if the item has one (existing item)
        if (item.id) {
          result.id = item.id;
        }

        return result;
      });

    const data: Omit<UpdateAssessment, 'id'> = {
      name: values.name,
      specialization: values.specialization || undefined,
      version: values.version,
      rules: buildAssessmentRules(),
      items: assessmentItems,
    };
    const res = await updateMutation.mutateAsync({ id: selectedItem.id, data });
    if (res.isSuccess) {
      setIsEditDialogOpen(false);
      setSelectedItem(null);
      editForm.reset();
      setItems([]);
      setActiveItemIndex(0);
      setRisks(DEFAULT_RISKS);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    const res = await deleteMutation.mutateAsync(selectedItem.id);
    if (res.isSuccess) {
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const openCreateDialog = () => {
    createForm.reset({ name: '', specialization: '', version: 1 });
    setItems([]);
    setActiveItemIndex(0);
    setRisks(DEFAULT_RISKS);
    setIsCreateDialogOpen(true);
  };

  // Parse rules back to form format
  const parseRulesToForm = (rules: AssessmentItemRule | undefined, answerIndex: number): AnswerRuleForm => {
    const isOptional = rules?.optionalAnswers?.[answerIndex] === true;
    const interval = rules?.intervalAnswers?.[answerIndex];
    const hasInterval = interval !== undefined;

    return {
      isOptional,
      hasInterval,
      min: interval?.min,
      max: interval?.max,
    };
  };

  const openEditDialog = (item: BaseAssessment) => {
    setSelectedItem(item);
    editForm.reset({
      name: item.name || '',
      specialization: item.specialization || '',
      version: item.version || 1,
    });
    // Load existing items with rules
    const existingItems: AssessmentItemForm[] = (item.items || []).map((it, index) => {
      const answerType = it.answerType === 'double' ? 'numeric' : it.answerType || 'string';
      const answers: AnswerOptionForm[] = (it.answers || []).map((ans, ansIndex) => ({
        displayValue: ans.displayValue || '',
        score: ans.score || 0,
        rule: parseRulesToForm(it.rules, ansIndex),
      }));

      // Handle case where there are interval rules but no answers (for numeric questions)
      if (it.rules?.intervalAnswers && Object.keys(it.rules.intervalAnswers).length > 0) {
        Object.keys(it.rules.intervalAnswers).forEach((key) => {
          const intervalIndex = parseInt(key, 10);
          // If there's an interval at this index but no corresponding answer, create a placeholder
          if (!answers[intervalIndex] && it.rules?.intervalAnswers?.[intervalIndex]) {
            const interval = it.rules.intervalAnswers[intervalIndex];
            // Determine type based on whether min/max are integers
            // Insert placeholder answer at the correct index
            while (answers.length <= intervalIndex) {
              answers.push({
                displayValue: '',
                score: 0,
                rule: createDefaultRule(),
              });
            }
            answers[intervalIndex] = {
              displayValue: '',
              score: 0,
              rule: {
                isOptional: false,
                hasInterval: true,
                min: interval.min,
                max: interval.max,
              },
            };
          }
        });
      }

      return {
        id: it.id,
        tempId: generateTempId(),
        orderIndex: it.orderIndex ?? index + 1,
        question: it.question || '',
        registryTerm: it.registryTerm || '',
        answerType,
        answerPersonProfile: it.answerPersonProfile || '',
        answers: answers,
      };
    });
    setItems(existingItems);
    setActiveItemIndex(existingItems.length > 0 ? 0 : -1);
    
    // Load existing risks
    if (item.rules?.intervalScores) {
      const loadedRisks: RiskForm = {
        low: item.rules.intervalScores.low || DEFAULT_RISKS.low,
        medium: item.rules.intervalScores.medium || DEFAULT_RISKS.medium,
        high: item.rules.intervalScores.high || DEFAULT_RISKS.high,
        veryHigh: item.rules.intervalScores.veryHigh || DEFAULT_RISKS.veryHigh,
      };
      setRisks(loadedRisks);
    } else {
      setRisks(DEFAULT_RISKS);
    }
    
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (item: BaseAssessment) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  };

  // Render the items editor (shared between create and edit dialogs)
  const renderItemsEditor = () => (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <Label className='text-base font-semibold'>Întrebări ({items.length})</Label>
        <Button type='button' variant='outline' size='sm' onClick={addItem}>
          <Plus className='h-4 w-4 mr-1' />
          Adaugă Întrebare
        </Button>
      </div>

      {items.length === 0 ? (
        <div className='text-center py-8 text-muted-foreground border rounded-lg border-dashed'>
          <HelpCircle className='h-8 w-8 mx-auto mb-2 opacity-50' />
          <p className='text-sm'>Nu există întrebări.</p>
          <p className='text-xs'>Adăugați prima întrebare folosind butonul de mai sus.</p>
        </div>
      ) : (
        <div className='flex gap-3'>
          {/* Items List with Drag and Drop */}
          <div className='w-52 shrink-0'>
            <div className='text-xs text-muted-foreground mb-1 px-1'>Trageți pentru a reordona</div>
            <ScrollArea className='h-[350px] border rounded-lg'>
              <div className='p-2 space-y-1'>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={items.map((item) => item.tempId)} strategy={verticalListSortingStrategy}>
                    {items.map((item, index) => (
                      <SortableQuestionItem
                        key={item.tempId}
                        item={item}
                        index={index}
                        isActive={activeItemIndex === index}
                        onClick={() => setActiveItemIndex(index)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </ScrollArea>
          </div>

          {/* Active Item Editor */}
          <div className='flex-1 border rounded-lg p-4'>
            {activeItemIndex >= 0 && activeItemIndex < items.length ? (
              <ScrollArea className='h-[340px] pr-3'>
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <Label className='font-medium'>Întrebare {activeItemIndex + 1}</Label>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='text-destructive hover:text-destructive hover:bg-destructive/10'
                      onClick={() => removeItem(activeItemIndex)}
                    >
                      <Trash2 className='h-4 w-4 mr-1' />
                      Șterge
                    </Button>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor={`item-order-${activeItemIndex}`} className='text-sm'>
                      Tip
                    </Label>
                    <Select
                      value={items[activeItemIndex].answerType}
                      onValueChange={(value) => handleAnswerTypeChange(activeItemIndex, value)}
                    >
                      <SelectTrigger className='h-9 text-xs'>
                        <SelectValue placeholder='Tip' />
                      </SelectTrigger>
                      <SelectContent>
                        {VALUE_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {getValueTypeLabel(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor={`item-answer-person-profile-${activeItemIndex}`} className='text-sm'>
                      Profil persoană
                    </Label>
                    <Select
                      value={items[activeItemIndex].answerPersonProfile || 'none'}
                      onValueChange={(value) =>
                        updateItem(activeItemIndex, 'answerPersonProfile', value === 'none' ? '' : value)
                      }
                    >
                      <SelectTrigger className='h-9 text-xs'>
                        <SelectValue placeholder='Selectează profilul' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='none'>Niciunul</SelectItem>
                        {PERSON_PROFILE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor={`item-registry-term-${activeItemIndex}`} className='text-sm'>
                      Termen registru
                    </Label>
                    <Select
                      value={items[activeItemIndex].registryTerm || 'none'}
                      onValueChange={(value) =>
                        updateItem(activeItemIndex, 'registryTerm', value === 'none' ? '' : value)
                      }
                    >
                      <SelectTrigger className='h-9 text-xs'>
                        <SelectValue placeholder='Selectează termenul' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='none'>Niciunul</SelectItem>
                        {registryTerms &&
                          Object.entries(registryTerms).map(([key, def]) => (
                            <SelectItem key={key} value={key}>
                              {def?.labels?.ro ?? def?.labels?.en ?? key}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor={`item-order-${activeItemIndex}`} className='text-sm'>
                      Ordine
                    </Label>
                    <Input
                      id={`item-order-${activeItemIndex}`}
                      type='number'
                      value={items[activeItemIndex].orderIndex}
                      onChange={(e) => updateItem(activeItemIndex, 'orderIndex', parseInt(e.target.value) || 0)}
                      className='h-9'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor={`item-question-${activeItemIndex}`} className='text-sm'>
                      Întrebare *
                    </Label>
                    <Input
                      id={`item-question-${activeItemIndex}`}
                      value={items[activeItemIndex].question}
                      onChange={(e) => updateItem(activeItemIndex, 'question', e.target.value)}
                      placeholder='Introduceți întrebarea...'
                      className='h-9'
                    />
                  </div>

                  <AnswerOptionsSection
                    options={items[activeItemIndex].answers}
                    onAdd={() => addAnswerOption(activeItemIndex)}
                    onUpdate={(ansIndex, field, value) => updateAnswerOption(activeItemIndex, ansIndex, field, value)}
                    onRemove={(ansIndex) => removeAnswerOption(activeItemIndex, ansIndex)}
                    idPrefix={`item-${activeItemIndex}`}
                    answerType={items[activeItemIndex].answerType}
                  />
                </div>
              </ScrollArea>
            ) : (
              <div className='h-full flex items-center justify-center text-muted-foreground'>
                <p className='text-sm'>Selectați o întrebare din listă</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>Evaluări</h1>
          <p className='text-muted-foreground mt-2'>Gestionați evaluările și întrebările asociate.</p>
        </div>
        {canCreate() && (
          <Button onClick={openCreateDialog}>
            <Plus className='h-4 w-4 mr-2' />
            Adaugă Evaluare
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <ClipboardList className='h-5 w-5' />
            Lista Evaluări
          </CardTitle>
          <CardDescription>{totalCount} evaluări în total</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex gap-2 mb-4'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
              <Input
                placeholder='Caută evaluări...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className='pl-10'
              />
              {searchInput && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0'
                  onClick={clearSearch}
                >
                  <X className='h-4 w-4' />
                </Button>
              )}
            </div>
            <Button onClick={handleSearch} variant='secondary'>
              <Search className='h-4 w-4 mr-2' />
              Caută
            </Button>
          </div>

          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : assessments.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-8 text-center'>
              <ClipboardList className='h-12 w-12 text-muted-foreground mb-4' />
              <p className='text-muted-foreground'>{searchTerm ? 'Nu s-au găsit evaluări.' : 'Nu există evaluări.'}</p>
            </div>
          ) : (
            <>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nume</TableHead>
                      <TableHead>Specializare</TableHead>
                      <TableHead>Versiune</TableHead>
                      <TableHead>Întrebări</TableHead>
                      <TableHead className='w-[100px]'>Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assessments.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className='font-medium'>{item.name || '-'}</TableCell>
                        <TableCell>
                          {item.specialization ? <Badge variant='secondary'>{item.specialization}</Badge> : '-'}
                        </TableCell>
                        <TableCell>{item.version || '-'}</TableCell>
                        <TableCell>
                          <Badge variant='outline'>{item.items?.length || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          {(canUpdate() || canDelete()) && (
                            <div className='flex items-center gap-1'>
                              {canUpdate() && (
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  className='h-8 w-8 p-0'
                                  onClick={() => openEditDialog(item)}
                                  title='Editează'
                                >
                                  <Edit className='h-4 w-4' />
                                </Button>
                              )}
                              {canDelete() && (
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  className='h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10'
                                  onClick={() => openDeleteDialog(item)}
                                  title='Șterge'
                                >
                                  <Trash2 className='h-4 w-4' />
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className='flex items-center justify-between mt-4'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm text-muted-foreground'>Rânduri pe pagină:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => updateParams({ size: parseInt(value, 10), page: 1 })}
                  >
                    <SelectTrigger className='w-[70px]'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='text-sm text-muted-foreground'>
                    Pagina {page} din {totalPages}
                  </span>
                  <div className='flex gap-1'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => updateParams({ page: page - 1 })}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => updateParams({ page: page + 1 })}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className='sm:max-w-[900px] max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Plus className='h-5 w-5' />
              Adaugă Evaluare
            </DialogTitle>
            <DialogDescription>Completați informațiile pentru noua evaluare și adăugați întrebările.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit(handleCreate)}>
            <Tabs defaultValue='general' className='w-full'>
              <TabsList className='grid w-full grid-cols-2'>
                <TabsTrigger value='general'>Informații Generale</TabsTrigger>
                <TabsTrigger value='items'>
                  Întrebări
                  {items.length > 0 && (
                    <Badge variant='secondary' className='ml-2'>
                      {items.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value='general' className='space-y-4 mt-4'>
                <div className='space-y-2'>
                  <Label htmlFor='create-name'>Nume *</Label>
                  <Input
                    id='create-name'
                    {...createForm.register('name')}
                    className={createForm.formState.errors.name ? 'border-destructive' : ''}
                  />
                  {createForm.formState.errors.name && (
                    <p className='text-sm text-destructive'>{createForm.formState.errors.name.message}</p>
                  )}
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='create-specialization'>Specializare</Label>
                  <Input id='create-specialization' {...createForm.register('specialization')} />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='create-version'>Versiune</Label>
                  <Input id='create-version' type='number' {...createForm.register('version')} />
                </div>
                <div className='space-y-2 pt-2'>
                  <Label>Riscuri</Label>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setIsRisksDialogOpen(true)}
                    className='w-full'
                  >
                    <AlertTriangle className='h-4 w-4 mr-2' />
                    Gestionează Riscuri
                  </Button>
                  {risks && (
                    <div className='mt-2 p-3 border rounded-lg bg-muted/30 space-y-1.5'>
                      <div className='text-xs font-medium text-muted-foreground mb-2'>Configurare curentă:</div>
                      <div className='grid grid-cols-2 gap-2 text-xs'>
                        <div>
                          <span className='font-medium'>Low:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.low.min ?? '-'} - {risks.low.max ?? '-'}
                          </span>
                        </div>
                        <div>
                          <span className='font-medium'>Medium:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.medium.min ?? '-'} - {risks.medium.max ?? '-'}
                          </span>
                        </div>
                        <div>
                          <span className='font-medium'>High:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.high.min ?? '-'} - {risks.high.max ?? '-'}
                          </span>
                        </div>
                        <div>
                          <span className='font-medium'>Very High:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.veryHigh.min ?? '-'} - {risks.veryHigh.max ?? '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value='items' className='mt-4'>
                {renderItemsEditor()}
              </TabsContent>
            </Tabs>

            <DialogFooter className='mt-6'>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  createForm.reset();
                  setItems([]);
                  setActiveItemIndex(0);
                  setRisks(DEFAULT_RISKS);
                }}
              >
                Anulează
              </Button>
              <Button type='submit' disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Se creează...
                  </>
                ) : (
                  'Creează'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className='sm:max-w-[900px] max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Edit className='h-5 w-5' />
              Editează Evaluare
            </DialogTitle>
            <DialogDescription>Modificați informațiile evaluării și întrebările asociate.</DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit(handleEdit)}>
            <Tabs defaultValue='general' className='w-full'>
              <TabsList className='grid w-full grid-cols-2'>
                <TabsTrigger value='general'>Informații Generale</TabsTrigger>
                <TabsTrigger value='items'>
                  Întrebări
                  {items.length > 0 && (
                    <Badge variant='secondary' className='ml-2'>
                      {items.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value='general' className='space-y-4 mt-4'>
                <div className='space-y-2'>
                  <Label htmlFor='edit-name'>Nume *</Label>
                  <Input
                    id='edit-name'
                    {...editForm.register('name')}
                    className={editForm.formState.errors.name ? 'border-destructive' : ''}
                  />
                  {editForm.formState.errors.name && (
                    <p className='text-sm text-destructive'>{editForm.formState.errors.name.message}</p>
                  )}
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='edit-specialization'>Specializare</Label>
                  <Input id='edit-specialization' {...editForm.register('specialization')} />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='edit-version'>Versiune</Label>
                  <Input id='edit-version' type='number' {...editForm.register('version')} />
                </div>
                <div className='space-y-2 pt-2'>
                  <Label>Riscuri</Label>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setIsRisksDialogOpen(true)}
                    className='w-full'
                  >
                    <AlertTriangle className='h-4 w-4 mr-2' />
                    Gestionează Riscuri
                  </Button>
                  {risks && (
                    <div className='mt-2 p-3 border rounded-lg bg-muted/30 space-y-1.5'>
                      <div className='text-xs font-medium text-muted-foreground mb-2'>Configurare curentă:</div>
                      <div className='grid grid-cols-2 gap-2 text-xs'>
                        <div>
                          <span className='font-medium'>Low:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.low.min ?? '-'} - {risks.low.max ?? '-'}
                          </span>
                        </div>
                        <div>
                          <span className='font-medium'>Medium:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.medium.min ?? '-'} - {risks.medium.max ?? '-'}
                          </span>
                        </div>
                        <div>
                          <span className='font-medium'>High:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.high.min ?? '-'} - {risks.high.max ?? '-'}
                          </span>
                        </div>
                        <div>
                          <span className='font-medium'>Very High:</span>{' '}
                          <span className='text-muted-foreground'>
                            {risks.veryHigh.min ?? '-'} - {risks.veryHigh.max ?? '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value='items' className='mt-4'>
                {renderItemsEditor()}
              </TabsContent>
            </Tabs>

            <DialogFooter className='mt-6'>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedItem(null);
                  editForm.reset();
                  setItems([]);
                  setActiveItemIndex(0);
                  setRisks(DEFAULT_RISKS);
                }}
              >
                Anulează
              </Button>
              <Button type='submit' disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Se salvează...
                  </>
                ) : (
                  'Salvează'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați ștergerea</AlertDialogTitle>
            <AlertDialogDescription>
              Sunteți sigur că doriți să ștergeți evaluarea <strong>{selectedItem?.name}</strong>?
              {selectedItem?.items && selectedItem.items.length > 0 && (
                <> Aceasta include {selectedItem.items.length} întrebări care vor fi de asemenea șterse.</>
              )}{' '}
              Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedItem(null)}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Se șterge...
                </>
              ) : (
                'Șterge'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Risks Dialog */}
      <Dialog open={isRisksDialogOpen} onOpenChange={setIsRisksDialogOpen}>
        <DialogContent className='sm:max-w-[600px]'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='h-5 w-5' />
              Gestionează Riscuri
            </DialogTitle>
            <DialogDescription>
              Definește intervalele pentru cele 4 tipuri de risc. Intervalele trebuie să fie secvențiale de la low la very high.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-6 py-4'>
            {/* Low Risk */}
            <div className='space-y-2'>
              <Label className='text-base font-medium'>Risc Scăzut (Low)</Label>
              <div className='flex items-center gap-2'>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Min:</Label>
                  <Input
                    type='number'
                    value={risks.low.min ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('low', 'min', value);
                    }}
                    className='h-9'
                    placeholder='1'
                  />
                </div>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Max:</Label>
                  <Input
                    type='number'
                    value={risks.low.max ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('low', 'max', value);
                    }}
                    className='h-9'
                    placeholder='25'
                  />
                </div>
              </div>
            </div>

            {/* Medium Risk */}
            <div className='space-y-2'>
              <Label className='text-base font-medium'>Risc Mediu (Medium)</Label>
              <div className='flex items-center gap-2'>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Min:</Label>
                  <Input
                    type='number'
                    value={risks.medium.min ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('medium', 'min', value);
                    }}
                    className='h-9'
                    placeholder='25'
                  />
                </div>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Max:</Label>
                  <Input
                    type='number'
                    value={risks.medium.max ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('medium', 'max', value);
                    }}
                    className='h-9'
                    placeholder='50'
                  />
                </div>
              </div>
            </div>

            {/* High Risk */}
            <div className='space-y-2'>
              <Label className='text-base font-medium'>Risc Ridicat (High)</Label>
              <div className='flex items-center gap-2'>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Min:</Label>
                  <Input
                    type='number'
                    value={risks.high.min ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('high', 'min', value);
                    }}
                    className='h-9'
                    placeholder='50'
                  />
                </div>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Max:</Label>
                  <Input
                    type='number'
                    value={risks.high.max ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('high', 'max', value);
                    }}
                    className='h-9'
                    placeholder='75'
                  />
                </div>
              </div>
            </div>

            {/* Very High Risk */}
            <div className='space-y-2'>
              <Label className='text-base font-medium'>Risc Foarte Ridicat (Very High)</Label>
              <div className='flex items-center gap-2'>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Min:</Label>
                  <Input
                    type='number'
                    value={risks.veryHigh.min ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('veryHigh', 'min', value);
                    }}
                    className='h-9'
                    placeholder='75'
                  />
                </div>
                <div className='flex items-center gap-1 flex-1'>
                  <Label className='text-sm text-muted-foreground w-12'>Max:</Label>
                  <Input
                    type='number'
                    value={risks.veryHigh.max ?? ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : undefined;
                      updateRisk('veryHigh', 'max', value);
                    }}
                    className='h-9'
                    placeholder='100'
                  />
                </div>
              </div>
            </div>

            {/* Validation Info */}
            <div className='pt-2 border-t'>
              <p className='text-xs text-muted-foreground'>
                <strong>Notă:</strong> Intervalele trebuie să fie secvențiale: low.max = medium.min, medium.max = high.min, high.max = veryHigh.min
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                setRisks(DEFAULT_RISKS);
              }}
            >
              Resetează la Default
            </Button>
            <Button type='button' onClick={() => setIsRisksDialogOpen(false)}>
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
